import { useState } from "react";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";

import { Table, Card, showSnackbar } from "@cofano/cck";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { useConfirm } from "components/hooks/useConfirm";

import { AdminSupportEntry } from "view/admin/utils/admin.types";

import SupportEntryBulkImportDialog from "../dialogs/SupportEntryBulkImportDialog";
import SupportEntryCreateEditDialog from "../dialogs/SupportEntryCreateEditDialog";

import { GetFaqEntriesDocument } from "__generated__/view/admin/graphql/admin.queries.generated";
import { DeleteAdminFaqEntryDocument } from "__generated__/view/admin/graphql/admin.mutations.generated";

const AdminSupportEntries = () => {

	const confirm = useConfirm();

	const { data, refetch, loading } = useQuery(GetFaqEntriesDocument);
	const [ deleteAdminFaqEntry ] = useMutation(DeleteAdminFaqEntryDocument);
	const [ bulkImportDialogOpen, openBulkImportDialog ] = useState(false);

	const [ faqEntryDialog, setFaqEntryDialog ] = useState<{
		open: boolean,
		entry: AdminSupportEntry | null
	}>({
		open: false,
		entry: null,
	});

	const content = (data?.faqEntries ?? []).map(it => ({ ...it, id: it.id ?? undefined }));

	const deleteFaqRule = (id: number) => {
		deleteAdminFaqEntry({
			variables: {
				id,
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_DELETED" }), { variant: "success" });
			refetch();
		});
	};

	return (
		<Card title={intl.formatMessage({ id: "ADMIN_FAQ_ENTRIES_TITLE" })}>
			<Table
				key={content.length}
				id="adminSupportFaqRules"
				disableFooter
				contentLoading={loading}
				content={content}
				columns={[ {
					id: "category.name",
					title: intl.formatMessage({ id: "ADMIN_FAQ_ENTRY_CATEGORY_LABEL" }),
				}, {
					id: "question",
					title: intl.formatMessage({ id: "ADMIN_FAQ_ENTRY_QUESTION_LABEL" }),
				}, {
					id: "answer",
					title: intl.formatMessage({ id: "ADMIN_FAQ_ENTRY_ANSWER_LABEL" }),
				} ]}
				rowActions={[ {
					icon: <EditIcon />,
					onClick: (row: AdminSupportEntry) => setFaqEntryDialog({ open: true, entry: row }),
				}, {
					icon: <DeleteIcon />,
					onClick: (row: AdminSupportEntry) => {
						confirm({
							type: "DELETE",
						}).then(() => {
							if (row.id) {
								deleteFaqRule(row.id);
							}
						});
					},
				} ]}
				actions={[ {
					title: intl.formatMessage({ id: "ADMIN_FAQ_COPY_DATA" }),
					onClick: () => {
						navigator.clipboard.writeText(JSON.stringify(content));
						showSnackbar(intl.formatMessage({ id: "ADMIN_FAQ_COPIED" }), { variant: "success" });
					},
					pinned: true,
				}, {
					title: intl.formatMessage({ id: "ADMIN_FAQ_BULK_IMPORT_ACTION" }),
					onClick: () => openBulkImportDialog(true),
					pinned: true,
				}, {
					title: intl.formatMessage({ id: "ADMIN_FAQ_ENTRY_ADD" }),
					onClick: () => setFaqEntryDialog({ open: true, entry: null }),
					pinned: true,
				} ]} />

			<SupportEntryBulkImportDialog
				open={bulkImportDialogOpen}
				onClose={() => openBulkImportDialog(false)}
				refetch={() => refetch()} />

			<SupportEntryCreateEditDialog
				open={faqEntryDialog.open}
				entry={faqEntryDialog.entry}
				onClose={() => setFaqEntryDialog({ open: false, entry: null })}
				refetch={() => refetch()} />
		</Card>
	);
};

export default AdminSupportEntries;
